import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { patternsService } from "../../services/patterns";
import { audiencesService } from "../../services/audiences";
import { formatEasyTime } from "../../utils/time";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import HeroIcon from "../../components/common/HeroIcon";
import {
  EMPTY_SENTINEL,
  formatNumber,
  isViewsEmpty,
  FiltersPopover,
  PatternsTable,
} from "../../components/patterns";
import { patternLabelInSidebar } from "../../components/patterns/utils";
import { trackEvent } from "../../utils/tracking";
import PatternDetail from "../pattern";
import ModalRoute from "../../components/common/ModalRoute";
import PatternsInfoModal from "../../components/patterns/PatternsInfoModal";
import {
  getDefaultPatternFilters,
  shouldDefaultFilterMoreThanOneVideo,
  UPLOAD_TIMEFRAME_META_SUFFIX,
  UPLOAD_TIMEFRAME_OPTIONS,
} from "../../constants/patternFilters";
import { PATTERNS_URL_QUERY_ALLOWLIST, parsePatternsUrlSearch } from "../../constants/patternsUrlQuery";

const PATTERN_INFO_MODAL_SEEN_KEY = "patternInfoModalSeen";

const readInitialPatternFilters = () =>
  parsePatternsUrlSearch(typeof window !== "undefined" ? window.location.search : "");

const Patterns = ({ audience }) => {
  const { patternType, patternId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const patternDetailBaseUrl =
    audience?.hash && patternType != null && patternId != null
      ? `/audience/${audience.hash}/patterns/${encodeURIComponent(patternType)}/${encodeURIComponent(patternId)}/`
      : "";
  const patternDetailOnClose = () =>
    navigate(`/audience/${audience?.hash}/patterns/${encodeURIComponent(patternType || "")}/`);
  const [categories, setCategories] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [meta, setMeta] = useState(null);
  const [patternRequestId, setPatternRequestId] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [sortBy, setSortBy] = useState("likes");
  const [sortDir, setSortDir] = useState("desc");
  const urlFilterOverridesRef = useRef(readInitialPatternFilters());
  const [filterMoreThanOneVideo, setFilterMoreThanOneVideo] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [uploadTimeframe, setUploadTimeframe] = useState(() => urlFilterOverridesRef.current.uploadTimeframe);
  const [additionalCreatorHash, setAdditionalCreatorHash] = useState(() => urlFilterOverridesRef.current.creatorHash);
  const [showFiltersPopover, setShowFiltersPopover] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showMorePatternTypes, setShowMorePatternTypes] = useState(false);
  const [patternsInfoOpen, setPatternsInfoOpen] = useState(false);

  const dismissPatternsInfo = useCallback(() => {
    setPatternsInfoOpen(false);
    try {
      localStorage.setItem(PATTERN_INFO_MODAL_SEEN_KEY, "1");
    } catch (_) {
      /* ignore quota / private mode */
    }
  }, []);

  const defaultFilters = useMemo(() => getDefaultPatternFilters(audience), [audience]);

  useEffect(() => {
    if (!audience?.hash) return;
    if (!urlFilterOverridesRef.current.uploadTimeframeExplicit) {
      setUploadTimeframe(defaultFilters.uploadTimeframe);
    }
  }, [audience?.hash, defaultFilters.uploadTimeframe]);

  const activeFilterCount = (
    (filterMoreThanOneVideo ? 1 : 0)
    + (searchQuery.trim() ? 1 : 0)
    + (showOtherOptions ? 1 : 0)
    + (uploadTimeframe !== "all" ? 1 : 0)
    + (additionalCreatorHash ? 1 : 0)
  );

  const clearAllFilters = () => {
    setFilterMoreThanOneVideo(shouldDefaultFilterMoreThanOneVideo(patterns));
    setSearchQuery("");
    setShowOtherOptions(false);
    setUploadTimeframe(defaultFilters.uploadTimeframe);
    setAdditionalCreatorHash(null);
  };

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!PATTERNS_URL_QUERY_ALLOWLIST.some((k) => params.has(k))) return;
    navigate({ pathname: location.pathname, search: "" }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const availablePatternTypes = categories.flatMap((cat) =>
    (cat.pattern_types || [])
      .filter((pt) => pt.handler)
      .map((pt) => ({ ...pt, categoryId: cat.id, categoryLabel: cat.label }))
  );

  const selectedPatternType = availablePatternTypes.find((pt) => pt.id === patternType) ?? availablePatternTypes[0];

  useEffect(() => {
    if (selectedPatternType?.more_section) {
      setShowMorePatternTypes(true);
    }
  }, [selectedPatternType?.id, selectedPatternType?.more_section]);

  useEffect(() => {
    patternsService.getPatternCategories(
      (response) => {
        const data = response.data?.categories ?? response.data ?? [];
        setCategories(Array.isArray(data) ? data : []);
        setLoadingCategories(false);
      },
      () => setLoadingCategories(false)
    );
  }, []);

  useEffect(() => {
    if (!loadingCategories && availablePatternTypes.length > 0 && audience?.hash) {
      const typeValid = patternType && availablePatternTypes.some((pt) => pt.id === patternType);
      if (!typeValid) {
        navigate(`/audience/${audience.hash}/patterns/${encodeURIComponent(availablePatternTypes[0].id)}/`, { replace: true });
      }
    }
  }, [loadingCategories, availablePatternTypes, patternType, audience?.hash, navigate]);

  useLayoutEffect(() => {
    if (!audience?.hash || !selectedPatternType?.id) {
      setLoadingPatterns(false);
      return;
    }
    setLoadingPatterns(true);
  }, [audience?.hash, selectedPatternType?.id, uploadTimeframe, additionalCreatorHash]);

  useEffect(() => {
    if (!audience?.hash || !selectedPatternType?.id) return;

    const params = {
      audience_hash: audience.hash,
      pattern_type: selectedPatternType.id,
      upload_timeframe: uploadTimeframe,
      regenerate: false,
    };
    if (additionalCreatorHash) {
      params.creator_hash = additionalCreatorHash;
    }

    patternsService.getPatterns(
      params,
      (response) => {
        setPatterns(response.data?.patterns ?? []);
        setMeta(response.data?.meta ?? null);
        setPatternRequestId(response.data?.pattern_request_id ?? null);
        setLoadingPatterns(false);
      },
      () => setLoadingPatterns(false)
    );
  }, [audience?.hash, selectedPatternType?.id, uploadTimeframe, additionalCreatorHash]);

  // Auto-apply >1 video filter only when enough multi-video patterns exist for this view.
  useEffect(() => {
    if (loadingPatterns) return;
    setFilterMoreThanOneVideo(shouldDefaultFilterMoreThanOneVideo(patterns));
    // Omit `patterns`: recompute on load finish only, not on local list updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatternType?.id, loadingPatterns]);

  useEffect(() => {
    setPatternsInfoOpen(false);
  }, [audience?.hash]);

  useEffect(() => {
    if (!audience?.hash || loadingCategories || availablePatternTypes.length === 0) {
      return;
    }
    try {
      if (localStorage.getItem(PATTERN_INFO_MODAL_SEEN_KEY)) return;
    } catch (_) {
      return;
    }
    setPatternsInfoOpen(true);
  }, [audience?.hash, loadingCategories, availablePatternTypes.length]);

  useEffect(() => {
    if (!patternType) return;
    setSortBy("likes");
    setSortDir("desc");
    const scrollContainer = document.querySelector("main.overflow-y-auto");
    if (scrollContainer && typeof scrollContainer.scrollTo === "function") {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [patternType]);

  const isOtherOption = (p) => (p.name ?? p.id ?? "").includes("~");

  const sortedPatterns = useMemo(() => {
    let copy = [...patterns];
    if (!showOtherOptions) {
      copy = copy.filter((p) => !isOtherOption(p));
    }
    if (filterMoreThanOneVideo) {
      copy = copy.filter((p) => (p.stats?.video_count ?? 0) > 1);
    }
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      copy = copy.filter((p) => {
        const name = (p.name ?? p.id ?? "").toLowerCase();
        return name.includes(query);
      });
    }
    copy.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "pattern":
          va = (a.name ?? a.id ?? "").toLowerCase();
          vb = (b.name ?? b.id ?? "").toLowerCase();
          return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        case "count": {
          const ac = a.stats?.video_count;
          const bc = b.stats?.video_count;
          va = ac == null ? EMPTY_SENTINEL : ac;
          vb = bc == null ? EMPTY_SENTINEL : bc;
          break;
        }
        case "views": {
          const av = a.stats?.views;
          const bv = b.stats?.views;
          va = isViewsEmpty(av) ? EMPTY_SENTINEL : Number(av);
          vb = isViewsEmpty(bv) ? EMPTY_SENTINEL : Number(bv);
          break;
        }
        case "likes": {
          const al = a.stats?.likes;
          const bl = b.stats?.likes;
          va = al == null ? EMPTY_SENTINEL : al;
          vb = bl == null ? EMPTY_SENTINEL : bl;
          break;
        }
        case "comments": {
          const ac = a.stats?.comments;
          const bc = b.stats?.comments;
          va = ac == null ? EMPTY_SENTINEL : ac;
          vb = bc == null ? EMPTY_SENTINEL : bc;
          break;
        }
        case "views_outlier":
          va = a.creator_comparison?.views ?? -Infinity;
          vb = b.creator_comparison?.views ?? -Infinity;
          break;
        case "likes_outlier":
          va = a.creator_comparison?.likes ?? -Infinity;
          vb = b.creator_comparison?.likes ?? -Infinity;
          break;
        case "comments_outlier":
          va = a.creator_comparison?.comments ?? -Infinity;
          vb = b.creator_comparison?.comments ?? -Infinity;
          break;
        default:
          return 0;
      }
      if (sortBy.endsWith("_outlier")) {
        if (va === vb) return 0;
        if (va === -Infinity) return 1;
        if (vb === -Infinity) return -1;
      } else {
        if (va === EMPTY_SENTINEL && vb === EMPTY_SENTINEL) return 0;
        if (va === EMPTY_SENTINEL) return 1;
        if (vb === EMPTY_SENTINEL) return -1;
      }
      if (sortDir === "asc") return va < vb ? -1 : va > vb ? 1 : 0;
      return vb < va ? -1 : vb > va ? 1 : 0;
    });
    return copy;
  }, [patterns, sortBy, sortDir, showOtherOptions, filterMoreThanOneVideo, searchQuery]);

  const handleSelectType = (pt) => {
    navigate(`/audience/${audience?.hash}/patterns/${encodeURIComponent(pt.id)}/`);
  };

  const handleMobileTypeChange = (event) => {
    const nextType = availablePatternTypes.find((pt) => pt.id === event.target.value);
    if (nextType) {
      handleSelectType(nextType);
    }
  };

  const handleRegenerate = () => {
    if (!audience?.hash || !selectedPatternType?.id) return;
    setIsSyncing(true);
    setLoadingPatterns(true);
    const regenParams = {
      audience_hash: audience.hash,
      pattern_type: selectedPatternType.id,
      upload_timeframe: uploadTimeframe,
      regenerate: true,
    };
    if (additionalCreatorHash) {
      regenParams.creator_hash = additionalCreatorHash;
    }
    patternsService.getPatterns(
      regenParams,
      (response) => {
        setPatterns(response.data?.patterns ?? []);
        setMeta(response.data?.meta ?? null);
        setPatternRequestId(response.data?.pattern_request_id ?? null);
        setLoadingPatterns(false);
        setIsSyncing(false);
        trackEvent('sync_pattern', { audience_hash: audience.hash, pattern_type: selectedPatternType?.id, upload_timeframe: uploadTimeframe });
      },
      () => {
        setLoadingPatterns(false);
        setIsSyncing(false);
      }
    );
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "pattern" ? "asc" : "desc");
    }
  };

  const baseline = meta?.videos ?? null;
  const generatedTimeframeSuffix = UPLOAD_TIMEFRAME_META_SUFFIX[uploadTimeframe] ?? "";
  const generatedVideoCountLabel = meta?.video_count === 1 ? "video" : "videos";

  if (loadingCategories) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <OrbitalLoader size={128} />
      </div>
    );
  }

  if (availablePatternTypes.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-theme-text opacity-80">
          No pattern types available yet. Hashtags and more will appear here as they are enabled.
        </p>
      </div>
    );
  }

  const categoriesWithTypes = categories
    .map((cat) => ({
      ...cat,
      pattern_types: (cat.pattern_types || []).filter((pt) => pt.handler),
    }))
    .filter((cat) => cat.pattern_types.length > 0);

  const hiddenMoreCount = categoriesWithTypes.reduce(
    (sum, cat) => sum + (cat.pattern_types || []).filter((pt) => pt.more_section).length,
    0
  );

  const totalPatternTypesCount = categoriesWithTypes.reduce(
    (sum, cat) => sum + (cat.pattern_types || []).length,
    0
  );

  const visibleCategoriesWithTypes = (showMorePatternTypes
    ? categoriesWithTypes
    : categoriesWithTypes
        .map((cat) => ({
          ...cat,
          pattern_types: (cat.pattern_types || []).filter((pt) => !pt.more_section),
        }))
        .filter((cat) => cat.pattern_types.length > 0)
  );

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
      <div className="w-full lg:w-48 lg:flex-shrink-0">
        <div className="lg:hidden mb-2">
          <div className="rounded-md border border-theme-secondary bg-theme-main">
            <select
              value={selectedPatternType?.id ?? ""}
              onChange={handleMobileTypeChange}
              className="w-full px-3 py-2 rounded-md border-none text-sm bg-transparent text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
              aria-label="Select pattern type"
            >
              {categoriesWithTypes.map((cat) => (
                <optgroup key={cat.id} label={cat.label}>
                  {cat.pattern_types.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {patternLabelInSidebar(pt.label, cat.label)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          {/* <button
            type="button"
            onClick={() => setPatternsInfoOpen(true)}
            className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm border border-theme-secondary text-theme-text hover:bg-theme-main transition-colors"
            aria-label="How patterns work"
          >
            <svg className="w-4 h-4 text-theme-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
            <span>Info</span>
          </button> */}
        </div>

        <nav className="hidden lg:block space-y-4">
          {visibleCategoriesWithTypes.map((cat) => (
            <div key={cat.id}>
              <div className="text-xs font-medium text-theme-text opacity-60 uppercase tracking-wider mb-1.5">
                {cat.label}
              </div>
              <div className="space-y-0.5">
                {cat.pattern_types.map((pt) => (
                  <button
                    key={pt.id}
                    onClick={() => handleSelectType(pt)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                      selectedPatternType?.id === pt.id
                        ? "bg-theme-active-nav text-theme-text font-medium"
                        : "text-theme-text hover:bg-theme-secondary"
                    }`}
                  >
                    {pt.icon && (
                      <HeroIcon
                        name={pt.icon}
                        className={`w-4 h-4 flex-shrink-0 ${selectedPatternType?.id === pt.id ? "opacity-80" : "opacity-40"}`}
                      />
                    )}
                    <span className="flex-1 min-w-0">{patternLabelInSidebar(pt.label, cat.label)}</span>
                    {pt.smart && (
                      <span className="flex-shrink-0" title="AI grouping" aria-hidden>
                        <HeroIcon
                          name="brain"
                          className={`w-3.5 h-3.5 ${
                            selectedPatternType?.id === pt.id
                              ? "text-theme-primary opacity-90"
                              : "text-theme-text opacity-45"
                          }`}
                        />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!showMorePatternTypes && hiddenMoreCount > 0 && (
            <button
              type="button"
              onClick={() => setShowMorePatternTypes(true)}
              className="w-full text-left px-4 py-2 rounded-md text-sm border border-theme-secondary text-theme-text hover:bg-theme-main transition-colors"
            >
              Show {hiddenMoreCount} more
            </button>
          )}
          <button
            type="button"
            onClick={() => setPatternsInfoOpen(true)}
            className="mt-2 w-full flex items-center justify-start gap-2 text-left px-4 py-2 rounded-md text-sm border border-theme-secondary text-theme-text hover:bg-theme-main transition-colors"
            aria-label="How patterns work"
          >
            <svg className="w-4 h-4 text-theme-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
              />
            </svg>
            <span>Info</span>
          </button>
        </nav>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="hidden sm:flex text-xl font-semibold text-theme-text items-center gap-2">
            {selectedPatternType?.label_long ?? selectedPatternType?.label ?? "Patterns"}{" "}
            {loadingPatterns ? (
              <span className="font-normal text-base text-theme-text opacity-80" aria-label="Loading patterns">
                {selectedPatternType?.smart ? "Calculating (20s)..." : "Fetching..."}
              </span>
            ) : (
              <span className="font-normal text-base text-theme-text opacity-70">
                {/* ({sortedPatterns.length} / {patterns.length}) */}
                {sortedPatterns.length} Pattern{sortedPatterns.length !== 1 ? "s" : ""}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-end">
            <div className="relative">
              <button
                onClick={() => setShowFiltersPopover((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                  activeFilterCount > 0
                    ? "bg-theme-primary/10 border-theme-primary text-theme-primary"
                    : "bg-theme-main border-theme-secondary text-theme-text hover:border-theme-text hover:border-opacity-30"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-theme-primary text-theme-button">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <FiltersPopover
                isOpen={showFiltersPopover}
                onClose={() => setShowFiltersPopover(false)}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                uploadTimeframe={uploadTimeframe}
                setUploadTimeframe={setUploadTimeframe}
                uploadTimeframeOptions={UPLOAD_TIMEFRAME_OPTIONS}
                filterMoreThanOneVideo={filterMoreThanOneVideo}
                setFilterMoreThanOneVideo={setFilterMoreThanOneVideo}
                showOtherOptions={showOtherOptions}
                setShowOtherOptions={setShowOtherOptions}
                onClearAll={clearAllFilters}
                activeFilterCount={activeFilterCount}
                additionalFilters={
                  additionalCreatorHash
                    ? [
                        {
                          key: "creator",
                          label: `Creator ${additionalCreatorHash.slice(0, 8)}${additionalCreatorHash.length > 8 ? "…" : ""}`,
                          onRemove: () => setAdditionalCreatorHash(null),
                        },
                      ]
                    : []
                }
              />
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-theme-text flex-wrap justify-end">
              {meta?.generated_at && (
                <>
                  <span className="sm:hidden" title={meta.generated_at}>
                    {meta.video_count != null
                      ? `${meta.video_count} ${generatedVideoCountLabel} at ${formatEasyTime(meta.generated_at)}`
                      : `As of ${formatEasyTime(meta.generated_at)}`}
                  </span>
                  <span className="hidden sm:inline" title={meta.generated_at}>
                    As of {formatEasyTime(meta.generated_at)}
                    {meta.video_count != null && ` from ${meta.video_count} ${generatedVideoCountLabel}${generatedTimeframeSuffix}`}
                  </span>
                </>
              )}
              {(() => {
                const moreAvailable = (meta?.video_count_available ?? 0) - (meta?.video_count ?? 0);
                const hasMore = moreAvailable > 0;
                return hasMore ? (
                  <button
                    onClick={handleRegenerate}
                    disabled={loadingPatterns}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-theme-primary text-theme-button disabled:opacity-50 transition-colors shadow-sm"
                    title={`Sync to include ${moreAvailable} more video${moreAvailable !== 1 ? 's' : ''}`}
                  >
                    <svg className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="sm:hidden">{moreAvailable} more</span>
                    <span className="hidden sm:inline">{moreAvailable} more available</span>
                  </button>
                ) : (
                  <button
                    onClick={handleRegenerate}
                    disabled={loadingPatterns}
                    className="p-1.5 rounded hover:bg-theme-main text-theme-text opacity-70 hover:opacity-100 disabled:opacity-50"
                    title="Regenerate"
                    aria-label="Regenerate"
                  >
                    <svg className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                );
              })()}
            </div>
          </div>
        </div>

        {loadingPatterns && patterns.length === 0 ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <OrbitalLoader size={128} special={isSyncing} />
          </div>
        ) : patterns.length === 0 ? (
          <div className="text-center py-12 rounded-lg border border-theme-secondary bg-theme-main">
            <p className="text-theme-text opacity-80">No patterns found for this audience.</p>
            <p className="text-theme-text opacity-60 text-sm mt-2">
              Try regenerating or ensure the audience has high-fit videos with data.
            </p>
          </div>
        ) : sortedPatterns.length === 0 ? (
          <div className="text-center py-12 rounded-lg border border-theme-secondary bg-theme-main">
            <svg className="mx-auto w-12 h-12 text-theme-text opacity-30 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <p className="text-theme-text opacity-80 mb-1">All patterns filtered out</p>
            <p className="text-theme-text opacity-60 text-sm mb-4">
              {patterns.length} pattern{patterns.length !== 1 ? "s" : ""} hidden by current filter
            </p>
            <button
              onClick={clearAllFilters}
              className="px-4 py-2 text-sm font-medium rounded-md bg-theme-primary text-theme-button"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="relative rounded-lg">
            <div className={`${loadingPatterns ? "pointer-events-none select-none opacity-55 transition-opacity duration-200" : ""}`}>
              <PatternsTable
                patterns={sortedPatterns}
                baseline={baseline}
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
                audienceHash={audience?.hash}
                patternType={patternType}
              />
            </div>
          </div>
        )}
      </div>

      <ModalRoute
        path="/audience/:audienceHash/patterns/:patternType/:patternId/*"
        parentPath={`/audience/${audience?.hash}/patterns/${encodeURIComponent(patternType || "")}/`}
        noPadding
        element={
          <PatternDetail
            baseUrl={patternDetailBaseUrl}
            onClose={patternDetailOnClose}
            patterns={patterns}
            baseline={baseline}
            meta={meta}
            formatNumber={formatNumber}
            audience={audience}
            patternRequestId={patternRequestId}
            patternTypeLabel={selectedPatternType?.label_long ?? selectedPatternType?.label}
            patternType={selectedPatternType?.id}
          />
        }
        maxWidth="max-w-5xl"
        heightClass="h-[calc(90vh+60px)] max-h-[100vh]"
      />

      <PatternsInfoModal
        isOpen={patternsInfoOpen}
        onDismiss={dismissPatternsInfo}
        totalPatternTypesCount={totalPatternTypesCount}
      />
    </div>
  );
};

export default Patterns;
